import axios from 'axios';
import get from 'lodash/get';
import has from 'lodash/has';
import assign from 'lodash/assign';
import merge from 'lodash/merge';
import set from 'lodash/set';

import {
  API_CLIENT__START_REQUEST,
  API_CLIENT__END_REQUEST,
  API_CLIENT__ERROR,
} from '../applications/researcher/redux/constants';
import { getBeginType, getErrorType } from '../applications/researcher/redux/selectors';
import {
  bugsnagSetUser,
  bugsnagNotify,
  bugsnagLeaveBreadcrumb,
  bugsnagReportableStatus,
} from '../applications/researcher/util';
import { whenOnline } from './whenOnline';
import { getLocale } from '../translations/i18n';

const getDefaultConfig = () => {
  if (has(window, 'beagly.csrfToken')) {
    return {
      headers: {
        common: {
          'X-CSRF-Token': window.beagly.csrfToken,
        },
      },
    };
  }
  return {};
};

const client = axios.create(getDefaultConfig());

const isRetriableError = (error) => {
  if (!error || !error.toJSON) return false;
  const { code, config, status, message } = error.toJSON();
  if (!config.retry) return false;
  return (
    ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].indexOf(code) > -1 ||
    ['408', '425', '429', '502', '503', '504', '598', '599', '521', '522', '524'].includes(
      (status || 0).toString(),
    ) ||
    (message || '').includes('Network Error')
  );
};

client.interceptors.response.use((response) => {
  if (response.status) {
    return response;
  }
  throw new DOMException('The request was aborted', 'AbortError');
});

client.interceptors.response.use(undefined, async (error) => {
  if (!isRetriableError(error)) {
    throw error;
  }
  const { config } = error.toJSON();
  config.retry -= 1;

  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(client(config));
    }, config.retryDelay),
  );
});

export function configAuth(user) {
  const token = user && user.token ? `JWT ${user.token}` : '';
  set(client, 'defaults.headers.common.Authorization', token);
  bugsnagSetUser(user);
}

const resolver = (action) => (dispatch) => {
  const requestPayload = merge(
    {
      method: 'get',
      headers: get(client, ['defaults', 'headers', 'common'], {}),
      retry: 0,
      retryDelay: 3000,
    },
    { headers: { 'Accept-Language': getLocale() } },
    action,
  );
  const originalType = action.type;

  const startAction = assign({}, action, {
    apiClientType: API_CLIENT__START_REQUEST,
    originalType,
    type: getBeginType(originalType),
  });
  bugsnagLeaveBreadcrumb(action.type, startAction);

  dispatch(startAction);
  return whenOnline().then(() =>
    client
      .request(requestPayload)
      .then((response) => {
        const { data } = response;
        const successAction = assign({}, action, {
          apiClientType: API_CLIENT__END_REQUEST,
          data,
          requestData: action.data,
        });
        bugsnagLeaveBreadcrumb(originalType, successAction);
        dispatch(successAction);
        return Promise.resolve(data);
      })
      .catch((error) => {
        const errorAction = assign({}, action, {
          apiClientType: API_CLIENT__ERROR,
          error,
          originalType,
          type: getErrorType(originalType),
        });
        const status = get(error, 'response.status', 500);
        if (bugsnagReportableStatus(status)) {
          bugsnagNotify(error, { metadata: { response: error.response, errorAction } });
        }
        dispatch(errorAction);
        return Promise.reject(error);
      }),
  );
};

export default resolver;
